import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OptimizedInput } from '@/components/ui/optimized-input';
import AbsenceAlerts from '@/components/AbsenceAlerts';
import PrefecturaReportsSection from '@/components/PrefecturaReportsSection';
import usePolling from '@/hooks/usePolling';
import pollGet from '@/utils/pollGet';
import { toast } from 'sonner';
import { 
  BookOpen, CheckCircle, Clock, AlertCircle, 
  Image, ChevronDown, ChevronUp, Users, RefreshCw, FileImage, Lock, Eye, EyeOff,
  Bell, FileText, Megaphone, MapPin, Calendar, X, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorDashboard = ({ user, onLogout }) => {
  const [classes, setClasses] = useState([]);
  const [monitoredStudents, setMonitoredStudents] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [encuadreDialogOpen, setEncuadreDialogOpen] = useState(false);
  const [selectedEncuadre, setSelectedEncuadre] = useState(null);
  const [showEncuadresSection, setShowEncuadresSection] = useState(true);
  
  const [expandedAttendance, setExpandedAttendance] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [viewNote, setViewNote] = useState(null); // {text, subject, taskLabel} | null
  const [orientadorJustifications, setOrientadorJustifications] = useState({});
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Notification state
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [boardNotifications, setBoardNotifications] = useState([]);

  // Real-time "recently updated" highlight state
  const [changedCells, setChangedCells] = useState(() => new Set());
  const prevStudentDataRef = useRef(null);
  const changedClearRef = useRef(null);

  // Diff two studentData snapshots and return the set of changed cell keys.
  const computeChangedCells = (nextData, prevData) => {
    const changed = new Set();
    const diffSection = (arr, prevArr, prefix) => {
      (arr || []).forEach((sec) => {
        const prevSec = (prevArr || []).find((s) => s.class_id === sec.class_id);
        const recs = sec.records || {};
        Object.keys(recs).forEach((sid) => {
          const newRec = recs[sid] || {};
          const oldRec = (prevSec && prevSec.records && prevSec.records[sid]) || {};
          Object.keys(newRec).forEach((colId) => {
            if (String(newRec[colId] ?? '') !== String(oldRec[colId] ?? '')) {
              changed.add(`${prefix}:${sec.class_id}:${sid}:${colId}`);
            }
          });
        });
      });
    };
    diffSection(nextData.attendance, prevData.attendance, 'a');
    diffSection(nextData.tasks, prevData.tasks, 't');
    return changed;
  };

  const isCellChanged = (prefix, classId, studentId, colId) =>
    changedCells.has(`${prefix}:${classId}:${studentId}:${colId}`);

  const fetchData = useCallback(async (showRefresh = false, silent = false) => {
    if (showRefresh) setRefreshing(true);
    const token = localStorage.getItem('token');
    try {
      const [classesRes, studentsRes] = await Promise.all([
        pollGet(`${API}/classes`, { key: 'tutor-classes', conditional: silent, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tutor/monitored-students`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!classesRes.notModified) setClasses(classesRes.data);
      setMonitoredStudents(studentsRes.data);

      const haveClasses = classesRes.notModified || (classesRes.data && classesRes.data.length > 0);
      if (haveClasses) {
        const dataRes = await pollGet(`${API}/tutor/student-data`, {
          key: 'tutor-dash-student-data', conditional: silent, headers: { Authorization: `Bearer ${token}` }
        });
        // 304 -> nothing changed since last poll: keep current data, skip work.
        if (dataRes.notModified) {
          if (showRefresh) setRefreshing(false);
          return;
        }
        setStudentData(dataRes.data);
        // Real-time highlight: on silent polls, flash the cells that changed.
        if (silent && prevStudentDataRef.current) {
          const changed = computeChangedCells(dataRes.data, prevStudentDataRef.current);
          if (changed.size > 0) {
            setChangedCells(changed);
            if (changedClearRef.current) clearTimeout(changedClearRef.current);
            changedClearRef.current = setTimeout(() => setChangedCells(new Set()), 3200);
          }
        }
        prevStudentDataRef.current = dataRes.data;
        
        const justificationsMap = {};
        const ojList = dataRes.data.orientador_justifications || [];
        
        ojList.forEach(j => {
          (j.affected_classes || []).forEach(ac => {
            const classId = ac.class_id;
            if (!justificationsMap[classId]) {
              justificationsMap[classId] = [];
            }
            justificationsMap[classId].push({
              student_name: j.student_name,
              dates: j.dates,
              reason: j.reason,
              orientador_name: j.orientador_name
            });
          });
        });
        
        setOrientadorJustifications(justificationsMap);

        if (!silent) {
          const attExpanded = {};
          const taskExpanded = {};
          classesRes.data.forEach(cls => {
            attExpanded[cls.id] = true;
            taskExpanded[cls.id] = true;
          });
          setExpandedAttendance(attExpanded);
          setExpandedTasks(taskExpanded);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch pending notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tutor/notifications/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingNotifications(response.data || []);
      setCurrentNotificationIndex(0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Fetch board notifications (active citatorios + avisos)
  const fetchBoardNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tutor/notifications/board`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoardNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching board notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchNotifications();
    fetchBoardNotifications();
  }, [fetchData, fetchNotifications, fetchBoardNotifications]);

  // ---- Real-time auto-refresh (no manual reload needed) ----
  // Keep a ref of whether the notification modal is currently showing so a
  // background poll never interrupts a parent mid-reading.
  const pendingRef = useRef(0);
  useEffect(() => { pendingRef.current = pendingNotifications.length; }, [pendingNotifications]);

  const refreshRealtime = useCallback(() => {
    // Silent refresh: updates attendance/tasks/justifications WITHOUT resetting
    // the parent's expand/collapse state or showing a loading screen.
    fetchData(false, true);
    fetchBoardNotifications();
    // Only pull new citatorios/avisos popups when none is currently open.
    if (pendingRef.current === 0) fetchNotifications();
  }, [fetchData, fetchBoardNotifications, fetchNotifications]);

  usePolling(refreshRealtime, { intervalMs: 12000 });

  // Mark notification as read
  const markNotificationRead = async () => {
    const currentNotif = pendingNotifications[currentNotificationIndex];
    if (!currentNotif) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tutor/notifications/${currentNotif.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Move to next notification or close
      if (currentNotificationIndex < pendingNotifications.length - 1) {
        setCurrentNotificationIndex(prev => prev + 1);
      } else {
        setPendingNotifications([]);
        setCurrentNotificationIndex(0);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Error al procesar la notificación');
    }
  };

  // Dismiss aviso from the board
  const dismissBoardAviso = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tutor/notifications/${notifId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoardNotifications(prev => prev.filter(n => n.id !== notifId));
      toast.success('Aviso descartado');
    } catch (error) {
      toast.error('Error al descartar el aviso');
    }
  };

  const getStudentsInClass = (classId) => {
    if (!studentData || !studentData.students) return [];
    
    const classInfo = classes.find(c => c.id === classId);
    if (!classInfo) return [];
    
    const classStudentIds = new Set(classInfo.student_ids || []);
    
    // Deduplicate by name - only return students actually enrolled in THIS class
    const seenNames = new Set();
    const matchingStudents = [];
    
    studentData.students.forEach(s => {
      const nameUpper = s.name.toUpperCase().trim();
      if (seenNames.has(nameUpper)) return;
      
      if (classStudentIds.has(s.id)) {
        seenNames.add(nameUpper);
        matchingStudents.push(s);
      }
    });
    
    return matchingStudents;
  };

  const getStudentClassCount = (studentName) => {
    if (!classes || classes.length === 0) return 0;
    const nameUpper = studentName.toUpperCase().trim();
    // Find student IDs matching this name
    const studentIds = (studentData?.students || [])
      .filter(s => s.name.toUpperCase().trim() === nameUpper)
      .map(s => s.id);
    // Count classes where this student is actually enrolled
    return classes.filter(cls => 
      (cls.student_ids || []).some(sid => studentIds.includes(sid))
    ).length;
  };

  // Get classes that have encuadres
  const getClassesWithEncuadres = () => {
    return classes.filter(cls => 
      (cls.encuadre_images && cls.encuadre_images.length > 0) || 
      cls.encuadre_url ||
      (cls.encuadre_manual && cls.encuadre_manual.length > 0)
    );
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const toggleAttendanceExpand = (classId) => {
    setExpandedAttendance(prev => ({...prev, [classId]: !prev[classId]}));
  };

  const toggleTaskExpand = (classId) => {
    setExpandedTasks(prev => ({...prev, [classId]: !prev[classId]}));
  };

  const viewEncuadre = (cls) => {
    setSelectedEncuadre(cls);
    setEncuadreDialogOpen(true);
  };

  const getAttendanceColor = (value) => {
    switch(value) {
      case '1': return 'bg-green-100 text-green-700 border-green-200';
      case '0': return 'bg-red-100 text-red-700 border-red-200';
      case 'R': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'J': return 'bg-green-100 text-green-700 border-green-200';
      case 'JO': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-white text-slate-400 border-slate-200';
    }
  };

  const getTaskColor = (value) => {
    if (value === undefined || value === null || value === '') return 'bg-white text-slate-400 border-slate-200';
    const num = parseInt(String(value), 10);
    if (isNaN(num)) return 'bg-white text-slate-600 border-slate-200';
    if (num >= 80) return 'bg-green-100 text-green-700 border-green-200';
    if (num >= 60) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Notification Modal - Blocking */}
      {pendingNotifications.length > 0 && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent className="bg-white max-w-md max-h-[90vh]" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${
                pendingNotifications[currentNotificationIndex]?.notification_type === 'citatorio' 
                  ? 'text-rose-600' 
                  : 'text-amber-600'
              }`}>
                {(() => {
                  const notif = pendingNotifications[currentNotificationIndex];
                  const isTeacher = notif?.source_role === 'teacher';
                  const sourceLabel = isTeacher ? `Prof. ${notif?.created_by_name} - ${notif?.class_subject || ''}` : 'Control Escolar';
                  if (notif?.notification_type === 'citatorio') {
                    return (<><FileText className="w-5 h-5" />Citatorio de {sourceLabel}</>);
                  }
                  return (<><Megaphone className="w-5 h-5" />Aviso de {sourceLabel}</>);
                })()}
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 overflow-y-auto pr-1" style={{ maxHeight: '60vh' }} data-testid="tutor-notif-body">
              {pendingNotifications[currentNotificationIndex]?.notification_type === 'citatorio' ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border ${
                    pendingNotifications[currentNotificationIndex]?.notification_type === 'citatorio'
                      ? 'bg-rose-50 border-rose-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className="text-sm text-slate-700 mb-4">
                      Estimado padre/madre de familia o tutor, se le cita cordialmente a una reunión importante. 
                      Por favor, tome nota de los siguientes detalles:
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-rose-600" />
                        <span className="text-sm font-medium text-slate-700">Fecha:</span>
                        <span className="text-sm text-slate-900">
                          {pendingNotifications[currentNotificationIndex]?.fecha}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-rose-600" />
                        <span className="text-sm font-medium text-slate-700">Hora:</span>
                        <span className="text-sm text-slate-900">
                          {pendingNotifications[currentNotificationIndex]?.hora}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-rose-600" />
                        <span className="text-sm font-medium text-slate-700">Lugar:</span>
                        <span className="text-sm text-slate-900">
                          {pendingNotifications[currentNotificationIndex]?.lugar}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-rose-200">
                        <span className="text-sm font-medium text-slate-700">Motivo:</span>
                        <p className="text-sm text-slate-900 mt-1">
                          {pendingNotifications[currentNotificationIndex]?.motivo}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Enviado por: {pendingNotifications[currentNotificationIndex]?.created_by_name}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-slate-700 mb-3">
                      Estimado padre/madre de familia o tutor, se le hace llegar el siguiente aviso importante:
                    </p>
                    <p className="text-sm text-slate-900 whitespace-pre-wrap">
                      {pendingNotifications[currentNotificationIndex]?.mensaje}
                    </p>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Enviado por: {pendingNotifications[currentNotificationIndex]?.created_by_name}
                  </p>
                </div>
              )}
              
              {pendingNotifications.length > 1 && (
                <div className="mt-4 text-center">
                  <span className="text-sm text-slate-500">
                    Notificación {currentNotificationIndex + 1} de {pendingNotifications.length}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex justify-center">
              <Button
                onClick={markNotificationRead}
                data-testid="tutor-notif-enterado-btn"
                className={`${
                  pendingNotifications[currentNotificationIndex]?.notification_type === 'citatorio'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Enterado
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Panel de Tutor</h1>
            <p className="text-slate-600">Monitoreo de asistencia y tareas</p>
          </div>
          <div className="flex items-center gap-2">
            {changedCells.size > 0 && (
              <span
                className="updated-badge-in inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold border border-green-200"
                data-testid="tutor-updated-indicator"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Actualizado
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Absence Alerts */}
        <div className="mb-6">
          <AbsenceAlerts title="Alertas de Inasistencias de mis Hijos" />
        </div>

        {/* Reportes de Prefectura de mis hijos (solo lectura) */}
        <PrefecturaReportsSection showStatus />

        {/* Sticky Notification Board - Always visible */}
          <div className="sticky top-0 z-30 mb-6" data-testid="notification-board">
            <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl shadow-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-slate-800 to-slate-700">
                <Bell className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-white">Tablero de Notificaciones</span>
                {boardNotifications.length > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {boardNotifications.length}
                  </span>
                )}
              </div>
              {boardNotifications.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Bell className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Sin notificaciones</p>
                </div>
              ) : (
              <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
                {boardNotifications.map((notif) => {
                  const isCitatorio = notif.notification_type === 'citatorio';
                  const isTeacher = notif.source_role === 'teacher';
                  const sourceLabel = isTeacher
                    ? `Prof. ${notif.created_by_name}${notif.class_subject ? ` - ${notif.class_subject}` : ''}`
                    : `Control Escolar${notif.created_by_name ? ` (${notif.created_by_name})` : ''}`;
                  
                  return (
                    <div key={notif.id} className={`flex items-start gap-3 px-4 py-3 ${isCitatorio ? 'bg-rose-50/60' : 'bg-sky-50/60'}`} data-testid={`board-notif-${notif.id}`}>
                      <div className={`shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${isCitatorio ? 'bg-rose-100' : 'bg-sky-100'}`}>
                        {isCitatorio
                          ? <FileText className="w-4 h-4 text-rose-600" />
                          : <Megaphone className="w-4 h-4 text-sky-600" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold uppercase tracking-wide ${isCitatorio ? 'text-rose-600' : 'text-sky-600'}`}>
                            {isCitatorio ? 'Citatorio' : 'Aviso'}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isTeacher ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {sourceLabel}
                          </span>
                        </div>
                        {isCitatorio ? (
                          <div className="mt-1 text-sm text-slate-700">
                            <div className="flex items-center gap-3 flex-wrap text-xs">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{notif.fecha}</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{notif.hora}</span>
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{notif.lugar}</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{notif.motivo}</p>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-slate-700 line-clamp-2">{notif.mensaje}</p>
                        )}
                      </div>
                      {/* Only avisos can be dismissed */}
                      {!isCitatorio && (
                        <button
                          onClick={() => dismissBoardAviso(notif.id)}
                          className="shrink-0 mt-0.5 p-1 rounded hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600"
                          title="Descartar aviso"
                          data-testid={`dismiss-notif-${notif.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>

        {/* Monitored Students Summary */}
        {monitoredStudents.length > 0 && (
          <Card className="p-4 mb-6 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-orange-600" />
              <div>
                <span className="font-medium text-slate-900">
                  {monitoredStudents.length === 1 ? 'Alumno Monitoreado' : 'Alumnos Monitoreados'}:
                </span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {monitoredStudents.map((s, idx) => {
                    const classCount = getStudentClassCount(s.student_name);
                    return (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full text-sm border border-orange-200">
                        {s.student_name}
                        <span className="w-5 h-5 bg-orange-100 text-orange-700 rounded-full text-xs flex items-center justify-center">
                          {classCount}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Encuadres Section */}
        {getClassesWithEncuadres().length > 0 && (
          <Card className="mb-6 overflow-hidden border-purple-200">
            <div 
              className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-200 cursor-pointer"
              onClick={() => setShowEncuadresSection(!showEncuadresSection)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileImage className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-slate-900">
                    Encuadres Disponibles ({getClassesWithEncuadres().length})
                  </span>
                  <span className="text-sm text-slate-500">
                    - Información de las materias
                  </span>
                </div>
                {showEncuadresSection ? 
                  <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                }
              </div>
            </div>
            
            {showEncuadresSection && (
              <div className="p-4 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {getClassesWithEncuadres().map((cls) => (
                    <div 
                      key={cls.id}
                      className="p-3 rounded-lg border border-purple-100 bg-purple-50/50 hover:bg-purple-100 transition-colors cursor-pointer group"
                      onClick={() => viewEncuadre(cls)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-200 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-300 transition-colors">
                          <Image className="w-5 h-5 text-purple-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{cls.subject}</h4>
                          <p className="text-xs text-slate-500">Grupo: {cls.group}</p>
                          <p className="text-xs text-slate-400">Prof. {cls.teacher_name}</p>
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-xs text-purple-600 font-medium">
                              {(cls.encuadre_images?.length > 0 || cls.encuadre_url)
                                ? `${cls.encuadre_images?.length || 1} ${(cls.encuadre_images?.length || 1) === 1 ? 'imagen' : 'imágenes'}`
                                : `${cls.encuadre_manual?.length || 0} criterio${(cls.encuadre_manual?.length || 0) === 1 ? '' : 's'}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Password Change Section */}
        <Card className="mb-6 overflow-hidden border-slate-200">
          <div 
            className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-slate-200 cursor-pointer"
            onClick={() => setShowPasswordSection(!showPasswordSection)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-slate-600" />
                <span className="font-semibold text-slate-900">
                  Cambiar Contraseña
                </span>
                <span className="text-sm text-slate-500">
                  - Actualiza tu contraseña de acceso
                </span>
              </div>
              {showPasswordSection ? 
                <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                <ChevronDown className="w-5 h-5 text-slate-400" />
              }
            </div>
          </div>
          
          {showPasswordSection && (
            <div className="p-4 bg-white">
              <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contraseña Actual
                  </label>
                  <div className="relative">
                    <OptimizedInput
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ingresa tu contraseña actual"
                      debounceMs={100}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nueva Contraseña
                  </label>
                  <div className="relative">
                    <OptimizedInput
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                      debounceMs={100}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirmar Nueva Contraseña
                  </label>
                  <div className="relative">
                    <OptimizedInput
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Repite la nueva contraseña"
                      minLength={6}
                      debounceMs={100}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {changingPassword ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 mt-2">
                  * Al cambiar tu contraseña, también se actualizará en el sistema de Control Escolar.
                </p>
              </form>
            </div>
          )}
        </Card>

        {/* Classes Content */}
        <div className="space-y-6">
          {classes.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">Esperando que los profesores agreguen clases</p>
              <p className="text-sm text-slate-500">
                {monitoredStudents.length > 0 
                  ? `Tus alumnos vinculados (${monitoredStudents.map(s => s.student_name).join(', ')}) aún no han sido agregados a ninguna clase.`
                  : 'Cuando los profesores creen clases y agreguen a tus hijos, podrás ver su información aquí.'
                }
              </p>
            </Card>
          ) : (
            <>
              {/* Classes Summary */}
              <div className="flex items-center gap-3 text-slate-700">
                <BookOpen className="w-5 h-5 text-orange-500" />
                <span className="font-medium">Clases Agregadas ({classes.length})</span>
              </div>

              {/* Class Cards */}
              {classes.map((cls, classIndex) => {
                const studentsInThisClass = getStudentsInClass(cls.id);
                
                return (
                  <div key={cls.id}>
                    {/* Separator between classes */}
                    {classIndex > 0 && (
                      <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                        <div className="px-3 py-1 bg-slate-100 rounded-full">
                          <span className="text-xs font-medium text-slate-500">Clase {classIndex + 1}</span>
                        </div>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                      </div>
                    )}
                    
                    <Card className="overflow-hidden shadow-md border-l-4 border-l-orange-400">
                      <div className="p-4 bg-gradient-to-r from-orange-50 via-white to-white border-b">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">{cls.subject}</h3>
                            <p className="text-sm text-slate-600">Grupo: {cls.group}</p>
                            <p className="text-sm text-slate-500">Prof. {cls.teacher_name}</p>
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            {studentsInThisClass.length > 0 && (
                              <div className="flex flex-wrap gap-1 justify-end">
                                {studentsInThisClass.map((s, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                    {s.name.split(' ').slice(-2).join(' ')}
                                  </span>
                                ))}
                              </div>
                            )}
                            {(cls.encuadre_images?.length > 0 || cls.encuadre_url || cls.encuadre_manual?.length > 0) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewEncuadre(cls)}
                                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                              >
                                <Image className="w-4 h-4 mr-1" />
                                Ver Encuadre
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                    {/* Attendance Grid */}
                    {studentData?.attendance && (
                      <div className="border-t-4 border-green-500">
                        <div className="p-4 bg-gradient-to-r from-green-50 to-white">
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => toggleAttendanceExpand(cls.id)}
                          >
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                Registro de Asistencias
                              </h4>
                              <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">1 = Asistencia</span>
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">0 = Falta</span>
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-medium">R = Retardo</span>
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">J = Justificada</span>
                                <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded font-medium">JO = Justificación Orientador</span>
                                <span className="px-2 py-1 bg-slate-50 text-slate-600 rounded font-medium inline-flex items-center gap-1.5" data-testid="tutor-attendance-legend-blank">
                                  <span className="inline-flex items-center justify-center w-5 h-5 bg-white text-slate-400 border border-slate-200 rounded text-[11px] leading-none">-</span>
                                  = Dato aún no registrado por profesor
                                </span>
                              </div>
                            </div>
                            {expandedAttendance[cls.id] ? 
                              <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            }
                          </div>
                        </div>
                        
                        {expandedAttendance[cls.id] && (() => {
                          const att = studentData.attendance.find(a => a.class_id === cls.id);
                          if (!att || !att.columns || att.columns.length === 0) {
                            return <p className="text-sm text-slate-500 p-4">Sin registros de asistencia</p>;
                          }
                          
                          // Filter students to only those enrolled in THIS class
                          const classStudentIds = new Set(cls.student_ids || []);
                          const seenNames = new Set();
                          const relevantStudents = studentData.students.filter(s => {
                            const nameUpper = s.name.toUpperCase().trim();
                            if (seenNames.has(nameUpper)) return false;
                            if (classStudentIds.has(s.id)) {
                              seenNames.add(nameUpper);
                              return true;
                            }
                            return false;
                          });
                          
                          if (relevantStudents.length === 0) {
                            return <p className="text-sm text-slate-500 p-4">No hay datos de tus alumnos</p>;
                          }

                          const classJustifications = orientadorJustifications[cls.id] || [];
                          
                          return (
                            <div className="p-2 overflow-x-auto" style={{ maxWidth: '100%' }}>
                              {(() => {
                                // Build attendance columns with cumulative % columns
                                const dispAttCols = [];
                                let attParcIdx = 0;
                                let attBlockCols = [];
                                for (let i = 0; i < att.columns.length; i++) {
                                  const col = att.columns[i];
                                  if (col.isSeparator) {
                                    attParcIdx++;
                                    dispAttCols.push({ _isAttCumulative: true, _parcialIndex: attParcIdx, _colIds: attBlockCols.map(c => c.id || c.date), label: `% P${attParcIdx}` });
                                    dispAttCols.push(col);
                                    attBlockCols = [];
                                  } else if (col.isSuspension) {
                                    // informative column, never counts toward %
                                    dispAttCols.push(col);
                                  } else {
                                    dispAttCols.push(col);
                                    attBlockCols.push(col);
                                  }
                                }
                                if (attBlockCols.length > 0) {
                                  attParcIdx++;
                                  const label = att.columns.some(c => c.isSeparator) ? `% P${attParcIdx}` : '% Total';
                                  dispAttCols.push({ _isAttCumulative: true, _parcialIndex: attParcIdx, _colIds: attBlockCols.map(c => c.id || c.date), label });
                                }

                                const calcTutorAttPct = (records, colIds, studentJODates, attColumns) => {
                                  let reg = 0, abs = 0;
                                  colIds.forEach(colId => {
                                    const col = attColumns.find(c => (c.id || c.date) === colId);
                                    const hasJO = col && (studentJODates.has(col.date) || studentJODates.has(col.label));
                                    if (hasJO) { reg++; }
                                    else {
                                      let val = (records[colId] || '').toUpperCase();
                                      if (val !== '') { reg++; if (val === '0') abs++; }
                                    }
                                  });
                                  if (reg === 0) return null;
                                  return Math.round(((reg - abs) / reg) * 100);
                                };

                                return (
                              <table className="border-collapse" style={{ minWidth: 'max-content' }}>
                                <thead>
                                  <tr className="border-b-2 border-slate-300 bg-slate-50">
                                    <th className="sticky left-0 z-10 bg-slate-50 text-left py-2 px-2 text-xs font-semibold text-slate-700 border-r-2 border-slate-300 w-[120px] min-w-[120px] max-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">Alumno</th>
                                    {dispAttCols.map((col, idx) => (
                                      col._isAttCumulative ? (
                                        <th key={`acum-${idx}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 border-r border-slate-200 min-w-[50px] max-w-[60px] bg-blue-50">
                                          <div className="flex flex-col items-center gap-0.5">
                                            <div className="font-bold text-blue-600 text-[10px]">{col.label}</div>
                                          </div>
                                        </th>
                                      ) : col.isSeparator ? (
                                        <th key={idx} className="text-center py-1 px-0 text-xs font-bold text-purple-700 border-r border-slate-200 w-[28px] min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-100 to-purple-200" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)' }}>
                                          <span className="text-purple-600 text-[8px] font-bold">{col.label}</span>
                                        </th>
                                      ) : col.isSuspension ? (
                                        <th key={idx}
                                          title={`Clase suspendida — ${col.motivo || 'sin motivo'}`}
                                          className="text-center py-2 px-1 text-xs font-semibold text-slate-700 border-r border-slate-200 min-w-[60px] max-w-[90px] whitespace-nowrap bg-red-50">
                                          <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-[11px]">{col.label}</span>
                                            <span className="text-[8px] font-bold text-red-500 leading-none uppercase">Susp.</span>
                                            {col.motivo && (
                                              <span className="block max-w-[82px] truncate text-[8px] font-normal normal-case text-red-600 leading-tight" title={col.motivo}>
                                                {col.motivo}
                                              </span>
                                            )}
                                          </div>
                                        </th>
                                      ) : (
                                        <th key={idx} className="text-center py-2 px-1 text-xs font-semibold text-slate-700 border-r border-slate-200 min-w-[44px] whitespace-nowrap bg-slate-50">
                                          {col.label}
                                        </th>
                                      )
                                    ))}
                                    <th className="text-center py-2 px-2 text-xs font-semibold text-slate-700 bg-slate-100 min-w-[60px]">% Faltas</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {relevantStudents.map((student, sIdx) => {
                                    const records = att.records[student.id] || {};
                                    
                                    const studentJODates = new Set();
                                    classJustifications.forEach(j => {
                                      if (j.student_name?.toUpperCase().trim() === student.name.toUpperCase().trim()) {
                                        j.dates?.forEach(d => {
                                          studentJODates.add(d);
                                          const [year, month, day] = d.split('-');
                                          studentJODates.add(`${day}/${month}`);
                                        });
                                      }
                                    });
                                    
                                    let absences = 0;
                                    let totalEvaluated = 0;
                                    
                                    att.columns.filter(c => !c.isSeparator && !c.isSuspension).forEach(col => {
                                      let val = records[col.id];
                                      const hasJO = studentJODates.has(col.date) || studentJODates.has(col.label);
                                      if (hasJO) val = 'JO';
                                      if (val === '1' || val === '0' || val === 'J' || val === 'JO' || val === 'R') {
                                        totalEvaluated++;
                                        if (val === '0') absences++;
                                      }
                                    });
                                    
                                    const absencePercent = totalEvaluated > 0 ? ((absences / totalEvaluated) * 100).toFixed(1) : '0.0';
                                    
                                    return (
                                      <tr key={sIdx} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium text-slate-900 border-r-2 border-slate-300 w-[120px] min-w-[120px] max-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                          <div className="whitespace-normal leading-tight break-words">{student.name}</div>
                                          {student.matricula && (
                                            <div className="text-[10px] text-slate-500 leading-tight">Mat: {student.matricula}</div>
                                          )}
                                        </td>
                                        {dispAttCols.map((col, colIdx) => {
                                          if (col._isAttCumulative) {
                                            const pct = calcTutorAttPct(records, col._colIds, studentJODates, att.columns);
                                            const color = pct === null ? 'bg-blue-50 text-slate-400 border-blue-200' : pct >= 80 ? 'bg-green-100 text-green-700 border-green-200' : pct >= 60 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200';
                                            return (
                                              <td key={`acum-${colIdx}`} className="text-center py-1 px-0 border-r border-slate-200 bg-blue-50">
                                                <div className={`w-12 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${color}`}>
                                                  {pct !== null ? `${pct}%` : '-'}
                                                </div>
                                              </td>
                                            );
                                          }
                                          if (col.isSeparator) {
                                            return (
                                              <td key={colIdx} className="text-center py-1 px-0 border-r border-slate-200 w-[28px] min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-50 to-purple-100" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.1) 2px, rgba(147, 51, 234, 0.1) 4px)' }}>
                                              </td>
                                            );
                                          }
                                          
                                          if (col.isSuspension) {
                                            return (
                                              <td key={colIdx} className="text-center py-1 px-1 border-r border-slate-200 bg-red-50"
                                                  title={`Clase suspendida — ${col.motivo || 'sin motivo'}`}>
                                                <span className="text-red-600 font-bold text-lg select-none">✕</span>
                                              </td>
                                            );
                                          }
                                          
                                          let value = records[col.id] || '-';
                                          
                                          const hasJO = classJustifications.some(j => 
                                            j.student_name?.toUpperCase().trim() === student.name.toUpperCase().trim() &&
                                            j.dates?.some(d => {
                                              if (d === col.date) return true;
                                              const [year, month, day] = d.split('-');
                                              const formattedJO = `${day}/${month}`;
                                              return formattedJO === col.label;
                                            })
                                          );
                                          
                                          if (hasJO) value = 'JO';
                                          
                                          return (
                                            <td key={colIdx} className="text-center py-1 px-0 border-r border-slate-200 bg-white">
                                              <div className={`w-9 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getAttendanceColor(value)} ${isCellChanged('a', cls.id, student.id, col.id) ? 'cell-updated-flash' : ''}`}>
                                                {value}
                                              </div>
                                            </td>
                                          );
                                        })}
                                        <td className="text-center py-1 px-1 bg-slate-50">
                                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${parseFloat(absencePercent) > 20 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                            {absencePercent}%
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Tasks Grid */}
                    {studentData?.tasks && (
                      <div className="border-t-4 border-amber-500">
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-white">
                          <div 
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => toggleTaskExpand(cls.id)}
                          >
                            <div className="flex items-center gap-3">
                              <h4 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-amber-600" />
                                Registro de Tareas
                              </h4>
                              <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">80-100% Excelente</span>
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-medium">60-79% Regular</span>
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">0-59% Bajo</span>
                                <span className="px-2 py-1 bg-slate-50 text-slate-600 rounded font-medium inline-flex items-center gap-1.5" data-testid="tutor-tasks-legend-blank">
                                  <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-50 text-slate-400 border border-blue-200 rounded text-[11px] leading-none">-</span>
                                  = Dato aún no registrado por profesor
                                </span>
                              </div>
                            </div>
                            {expandedTasks[cls.id] ? 
                              <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            }
                          </div>
                        </div>
                        
                        {expandedTasks[cls.id] && (() => {
                          const taskGrid = studentData.tasks.find(t => t.class_id === cls.id);
                          // Backend sends: columns (with description), records
                          // Frontend expects: columns or task_descriptions, records
                          const taskColumns = taskGrid?.columns || [];
                          const taskRecords = taskGrid?.records || {};
                          const taskNotes = taskGrid?.notes || {};
                          
                          if (!taskGrid || taskColumns.length === 0) {
                            return <p className="text-sm text-slate-500 p-4">Sin tareas registradas</p>;
                          }
                          
                          // Filter students to only those enrolled in THIS class
                          const taskClassStudentIds = new Set(cls.student_ids || []);
                          const seenTaskNames = new Set();
                          const relevantStudents = studentData.students.filter(s => {
                            const nameUpper = s.name.toUpperCase().trim();
                            if (seenTaskNames.has(nameUpper)) return false;
                            if (taskClassStudentIds.has(s.id)) {
                              seenTaskNames.add(nameUpper);
                              return true;
                            }
                            return false;
                          });
                          
                          if (relevantStudents.length === 0) {
                            return <p className="text-sm text-slate-500 p-4">No hay datos de tus alumnos</p>;
                          }
                          
                          return (
                            <div className="p-2 overflow-x-auto" style={{ maxWidth: '100%' }}>
                              {(() => {
                                // Build columns with cumulative % columns
                                const displayCols = [];
                                let parcialIdx = 0;
                                let blockTasks = [];
                                for (let i = 0; i < taskColumns.length; i++) {
                                  const col = taskColumns[i];
                                  if (col.isSeparator) {
                                    parcialIdx++;
                                    displayCols.push({ _isCumulative: true, _parcialIndex: parcialIdx, _taskIds: blockTasks.map(t => t.id), label: `% P${parcialIdx}` });
                                    displayCols.push(col);
                                    blockTasks = [];
                                  } else {
                                    displayCols.push(col);
                                    blockTasks.push(col);
                                  }
                                }
                                if (blockTasks.length > 0) {
                                  parcialIdx++;
                                  const label = taskColumns.some(c => c.isSeparator) ? `% P${parcialIdx}` : '% Total';
                                  displayCols.push({ _isCumulative: true, _parcialIndex: parcialIdx, _taskIds: blockTasks.map(t => t.id), label });
                                }

                                const calcAvg = (records, ids) => {
                                  const vals = ids.map(id => records[id]).filter(v => v !== undefined && v !== null && v !== '');
                                  if (vals.length === 0) return null;
                                  return Math.round(vals.reduce((a, v) => a + (parseInt(v, 10) || 0), 0) / vals.length);
                                };

                                return (
                              <table className="border-collapse" style={{ minWidth: 'max-content' }}>
                                <thead>
                                  <tr className="border-b-2 border-slate-300 bg-slate-50">
                                    <th className="sticky left-0 z-10 bg-slate-50 text-left py-2 px-2 text-xs font-semibold text-slate-700 border-r-2 border-slate-300 w-[120px] min-w-[120px] max-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">Alumno</th>
                                    {displayCols.map((col, idx) => (
                                      col._isCumulative ? (
                                        <th key={`cum-${idx}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 border-r border-slate-200 min-w-[50px] max-w-[60px] bg-blue-50">
                                          <div className="flex flex-col items-center gap-0.5">
                                            <div className="font-bold text-blue-600 text-[10px]">{col.label}</div>
                                          </div>
                                        </th>
                                      ) : col.isSeparator ? (
                                        <th key={idx} className="text-center py-1 px-0 text-xs font-bold text-purple-700 border-r border-slate-200 w-[28px] min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-100 to-purple-200" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)' }}>
                                          <span className="text-purple-600 text-[8px] font-bold">{col.label}</span>
                                        </th>
                                      ) : (
                                        <th key={idx} className="text-center py-2 px-1 text-xs font-semibold text-slate-700 border-r border-slate-200 min-w-[55px] max-w-[70px] bg-slate-50 group relative" title={col.description || ''}>
                                          <div className="flex flex-col items-center gap-0.5">
                                            <div className="font-bold text-slate-800">{col.number || (idx + 1)}</div>
                                            <div className="font-normal text-slate-500 text-[10px]">{col.date ? col.date.split('-').reverse().slice(0,2).join('/') : ''}</div>
                                            {col.description && (
                                              <div className="font-normal text-slate-500 text-[9px] line-clamp-2 max-w-[65px] cursor-help">
                                                {col.description}
                                              </div>
                                            )}
                                          </div>
                                          {col.description && col.description.length > 15 && (
                                            <div className="absolute hidden group-hover:block z-[100] bg-slate-800 text-white text-xs rounded px-2 py-1 top-full left-1/2 transform -translate-x-1/2 mt-1 whitespace-normal max-w-[200px] shadow-lg pointer-events-none">
                                              {col.description}
                                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-slate-800"></div>
                                            </div>
                                          )}
                                        </th>
                                      )
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {relevantStudents.map((student, sIdx) => {
                                    const studentRecords = taskRecords[student.id] || {};
                                    
                                    return (
                                      <tr key={sIdx} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium text-slate-900 border-r-2 border-slate-300 w-[120px] min-w-[120px] max-w-[120px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                          <div className="whitespace-normal leading-tight break-words">{student.name}</div>
                                          {student.matricula && (
                                            <div className="text-[10px] text-slate-500 leading-tight">Mat: {student.matricula}</div>
                                          )}
                                        </td>
                                        {displayCols.map((col, colIdx) => {
                                          if (col._isCumulative) {
                                            const avg = calcAvg(studentRecords, col._taskIds);
                                            const color = avg === null ? 'bg-blue-50 text-slate-400 border-blue-200' : avg >= 80 ? 'bg-green-100 text-green-700 border-green-200' : avg >= 60 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200';
                                            return (
                                              <td key={`cum-${colIdx}`} className="text-center py-1 px-0 border-r border-slate-200 bg-blue-50">
                                                <div className={`w-12 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${color}`}>
                                                  {avg !== null ? `${avg}%` : '-'}
                                                </div>
                                              </td>
                                            );
                                          }
                                          if (col.isSeparator) {
                                            return (
                                              <td key={colIdx} className="text-center py-1 px-0 border-r border-slate-200 w-[28px] min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-50 to-purple-100" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.1) 2px, rgba(147, 51, 234, 0.1) 4px)' }}>
                                              </td>
                                            );
                                          }
                                          const val = studentRecords[col.id];
                                          const displayVal = val !== undefined && val !== null && val !== '' ? `${val}%` : '-';
                                          const isEmpty = val === undefined || val === null || val === '';
                                          const todayStr = new Date().toISOString().slice(0, 10);
                                          const isPending = isEmpty && col.date && col.date >= todayStr;
                                          if (isPending) {
                                            return (
                                              <td key={colIdx} className="text-center py-1 px-0 border-r border-slate-200 bg-white">
                                                <div className="w-12 h-9 mx-auto flex flex-col items-center justify-center text-[9px] font-bold leading-tight border-2 rounded-lg bg-amber-100 text-amber-700 border-amber-300" title="Tarea pendiente">
                                                  <span>TAREA</span>
                                                  <span>PEND.</span>
                                                </div>
                                              </td>
                                            );
                                          }
                                          const noteText = (taskNotes[student.id] || {})[col.id];
                                          return (
                                            <td key={colIdx} className="text-center py-1 px-0 border-r border-slate-200 bg-white">
                                              {noteText ? (
                                                <button
                                                  type="button"
                                                  onClick={() => setViewNote({ text: noteText, subject: cls.subject, taskLabel: col.description || `Tarea ${col.number || ''}` })}
                                                  className={`relative w-12 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getTaskColor(val)} ${isCellChanged('t', cls.id, student.id, col.id) ? 'cell-updated-flash' : ''}`}
                                                  title={`Justificación: ${noteText}`}
                                                  data-testid={`tutor-task-note-${student.id}-${col.id}`}
                                                >
                                                  {displayVal}
                                                  <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-indigo-500 border border-white" />
                                                </button>
                                              ) : (
                                                <div className={`w-12 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg ${getTaskColor(val)} ${isCellChanged('t', cls.id, student.id, col.id) ? 'cell-updated-flash' : ''}`}>
                                                  {displayVal}
                                                </div>
                                              )}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </Card>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Encuadre Dialog */}
        <Dialog open={encuadreDialogOpen} onOpenChange={setEncuadreDialogOpen}>
          <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileImage className="w-5 h-5 text-purple-600" />
                Encuadre - {selectedEncuadre?.subject}
              </DialogTitle>
              <p className="text-sm text-slate-500">
                Grupo: {selectedEncuadre?.group} | Prof. {selectedEncuadre?.teacher_name}
              </p>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Handle encuadre_images array */}
              {selectedEncuadre?.encuadre_images?.map((img, idx) => (
                <div key={idx} className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                  <img
                    src={img}
                    alt={`Encuadre ${idx + 1}`}
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
              {/* Handle single encuadre_url */}
              {selectedEncuadre?.encuadre_url && !selectedEncuadre?.encuadre_images?.length && (
                <div className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden border border-slate-200 md:col-span-2">
                  <img
                    src={selectedEncuadre.encuadre_url}
                    alt="Encuadre"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>
            {/* Handle MANUAL encuadre (criterios) */}
            {selectedEncuadre?.encuadre_manual?.length > 0 && (
              <div className="mt-4 border border-purple-100 rounded-lg overflow-hidden" data-testid="tutor-encuadre-manual">
                <div className="flex items-center justify-between bg-purple-50 px-4 py-2 font-semibold text-purple-800 text-sm">
                  <span>Criterio de evaluación</span>
                  <span>Peso</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {selectedEncuadre.encuadre_manual.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-slate-800">{c.nombre}</span>
                      <span className="text-purple-700 font-semibold">
                        {c.peso ? (String(c.peso).includes('%') ? c.peso : `${c.peso}%`) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!selectedEncuadre?.encuadre_images?.length && !selectedEncuadre?.encuadre_url && !selectedEncuadre?.encuadre_manual?.length && (
              <div className="text-center py-8 text-slate-500">
                <FileImage className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No hay encuadre disponible</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Read-only task note viewer */}
        <Dialog open={!!viewNote} onOpenChange={(open) => { if (!open) setViewNote(null); }}>
          <DialogContent className="max-w-md" data-testid="tutor-note-view-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                Justificación de la calificación
              </DialogTitle>
            </DialogHeader>
            {viewNote && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">{viewNote.subject} — {viewNote.taskLabel}</p>
                <p className="text-sm text-slate-800 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">{viewNote.text}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TutorDashboard;
